import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Link,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SecurityIcon from '@mui/icons-material/Security';
import CookieIcon from '@mui/icons-material/Cookie';
import HomeIcon from '@mui/icons-material/Home';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';

const HeroSection = styled(Box)({
  background: 'linear-gradient(135deg, rgba(198, 40, 40, 0.05) 0%, rgba(142, 0, 0, 0.02) 100%)',
  padding: '80px 0 40px 0',
  // Pas de centrage global ici : ce conteneur enveloppe toute la page, et un
  // `textAlign: center` posé à ce niveau centrait chaque paragraphe du document,
  // ce qui rend un texte long pénible à lire. Le titre, les cartes et le pied de
  // page portent leur propre centrage.
  position: 'relative',
  overflow: 'hidden',
  width: '100%'
});

const PageContainer = styled(Container)({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 24px',
  '@media (max-width: 600px)': {
    padding: '0 16px'
  }
});

const StyledPaper = styled(Paper)({
  padding: '48px',
  borderRadius: '20px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(198, 40, 40, 0.1)',
  marginBottom: '32px',
  '@media (max-width: 600px)': {
    padding: '24px'
  }
});

const SectionTitle = styled(Typography)({
  fontWeight: 700,
  color: '#c62828',
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  fontSize: '1.75rem',
  '@media (max-width: 600px)': {
    fontSize: '1.5rem'
  }
});

const StyledAccordion = styled(Accordion)({
  boxShadow: 'none',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  borderRadius: '12px !important',
  marginBottom: '16px',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '16px 0',
    borderColor: '#c62828',
  },
});

const InfoCard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '24px',
  backgroundColor: '#f5f5f5',
  borderRadius: '16px',
  height: '100%',
  textAlign: 'center',
  border: '1px solid rgba(198, 40, 40, 0.1)',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.1)'
  }
});

const ContactCard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '32px 24px',
  backgroundColor: '#fff',
  borderRadius: '16px',
  height: '100%',
  textAlign: 'center',
  border: '1px solid #e0e0e0',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
});

const Badge = styled(Box)({
  backgroundColor: 'rgba(198, 40, 40, 0.1)',
  padding: '4px 12px',
  borderRadius: '20px',
  color: '#c62828',
  fontSize: '0.875rem',
  fontWeight: 500
});

const FooterNote = styled(Box)({
  marginTop: '48px',
  padding: '24px',
  backgroundColor: '#f8f8f8',
  borderRadius: '16px',
  border: '1px solid #e0e0e0',
  textAlign: 'center'
});

const PolitiqueConfidentialite = () => {
  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const currentYear = new Date().getFullYear();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <HeroSection>
        <PageContainer>
          <StyledPaper elevation={0}>
            {/* En-tête */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              mb: 4 
            }}>
              <PrivacyTipIcon 
                sx={{ 
                  fontSize: 80, 
                  color: '#c62828',
                  opacity: 0.8,
                  mb: 2
                }} 
              />
              <Typography variant="h1" sx={{ 
                fontWeight: 800, 
                mb: 2,
                color: '#333',
                fontSize: { xs: '2rem', md: '3rem' },
                textAlign: 'center'
              }}>
                Politique de confidentialité
              </Typography>
              <Typography variant="subtitle1" sx={{ color: '#666', textAlign: 'center', mb: 2 }}>
                Dernière mise à jour : 22/08/2026
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 2, 
                justifyContent: 'center' 
              }}>
                <Badge>🇨🇦 Conforme à la Loi 25 (Québec)</Badge>
                <Badge>Partenaire de la Croix-Rouge canadienne</Badge>
              </Box>
            </Box>

            <Divider sx={{ my: 4, borderColor: 'rgba(198, 40, 40, 0.2)' }} />

            {/* Introduction - Qui sommes-nous ? */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: 3,
              mb: 6,
              justifyContent: 'center'
            }}>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 0' } }}>
                <InfoCard>
                  <PersonIcon sx={{ fontSize: 40, color: '#c62828', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Responsable</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Amira Ben Nasr
                  </Typography>
                </InfoCard>
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 0' } }}>
                <InfoCard>
                  <SecurityIcon sx={{ fontSize: 40, color: '#c62828', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Activité</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Formation en secourisme
                  </Typography>
                </InfoCard>
              </Box>
            </Box>

            {/* Sections principales avec accordéons */}
            <Box sx={{ width: '100%', mb: 6 }}>
              {/* 1. Responsable du traitement */}
              <SectionTitle variant="h4">
                <GavelIcon /> 1. Responsable du traitement
              </SectionTitle>
              
              <StyledAccordion expanded={expanded === 'panel1'} onChange={handleAccordionChange('panel1')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>Qui est responsable de vos données ?</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Le site <strong>vitasecours.ca</strong> est exploité par :
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 1,
                    p: 2,
                    backgroundColor: '#fafafa',
                    borderRadius: '8px'
                  }}>
                    <Typography variant="body1">
                      <strong>Amira Ben Nasr</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon sx={{ fontSize: 18, color: '#c62828' }} />
                      275 Bd des Laurentides, Saint-Jérôme (QC) J7Z 7K8, Canada
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 18, color: '#c62828' }} />
                      <Link href="mailto:info@vitasecours.ca" sx={{ color: '#c62828' }}>info@vitasecours.ca</Link>
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    La responsable agit à titre de responsable de la protection des renseignements personnels conformément à la <strong>Loi 25 du Québec</strong>.
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>

              {/* 2. Nature des renseignements collectés */}
              <StyledAccordion expanded={expanded === 'panel2'} onChange={handleAccordionChange('panel2')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>2. Nature des renseignements collectés</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Nous collectons uniquement les renseignements nécessaires à l'inscription aux formations de secourisme :
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 2
                  }}>
                    <Box sx={{ flex: '1 1 200px' }}>
                      <Box component="ul" sx={{ color: '#666', lineHeight: 1.8, pl: 2, m: 0 }}>
                        <li>Nom et prénom</li>
                        <li>Date de naissance</li>
                        <li>Adresse postale</li>
                        <li>Ville, province, code postal</li>
                        <li>Numéro de téléphone</li>
                      </Box>
                    </Box>
                    <Box sx={{ flex: '1 1 200px' }}>
                      <Box component="ul" sx={{ color: '#666', lineHeight: 1.8, pl: 2, m: 0 }}>
                        <li>Adresse courriel</li>
                        <li>Nom du responsable légal (si mineur)</li>
                        <li>Date et informations d'inscription</li>
                        <li>Statut d'inscription</li>
                        <li>Organisme ou groupe, le cas échéant</li>
                      </Box>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2, fontWeight: 600 }}>
                    Renseignements facultatifs, demandés pour la sécurité pendant la formation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Certains champs touchent à la santé ou concernent des mineurs. Ils sont
                    facultatifs, servent uniquement au bon déroulement du cours, et ne sont
                    consultables que par notre personnel :
                  </Typography>
                  <Box component="ul" sx={{ color: '#666', lineHeight: 1.8, pl: 2, m: 0, mb: 2 }}>
                    <li>Allergies</li>
                    <li>Besoins particuliers</li>
                    <li>Personne à joindre en cas d'urgence</li>
                    <li>École fréquentée, pour les formations destinées aux jeunes</li>
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph sx={{ fontWeight: 600 }}>
                    Renseignements techniques
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Lorsqu'une donnée est créée ou modifiée dans notre système, nous conservons
                    l'adresse IP et le type de navigateur à l'origine de l'opération, à des fins
                    de sécurité. Voir la section « Journal de sécurité » plus bas.
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, color: '#c62828' }}>
                    Nous ne collectons aucune donnée biométrique. Aucun numéro de carte bancaire
                    n'est reçu ni conservé sur nos serveurs : le paiement est traité directement
                    par notre prestataire (voir la section « Paiements »).
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>

              {/* 3. Finalité de la collecte */}
              <StyledAccordion expanded={expanded === 'panel3'} onChange={handleAccordionChange('panel3')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>3. Finalité de la collecte</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Les renseignements sont collectés afin de :
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 1
                  }}>
                    {[
                      'Gérer les inscriptions aux formations',
                      'Organiser les sessions de formation',
                      'Émettre les certifications en partenariat avec la Croix-Rouge canadienne',
                      'Communiquer avec les participants',
                      'Assurer la conformité administrative',
                      'Traiter les demandes d\'annulation ou de remboursement'
                    ].map((item, index) => (
                      <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <span style={{ color: '#c62828', fontWeight: 700 }}>•</span> {item}
                      </Typography>
                    ))}
                  </Box>
                </AccordionDetails>
              </StyledAccordion>

              {/* 4. Paiements */}
              <StyledAccordion expanded={expanded === 'panel4'} onChange={handleAccordionChange('panel4')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>4. Paiements</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Deux moyens de paiement sont proposés : la carte bancaire en ligne
                    et le virement Interac.</strong> Dans les deux cas, en dollars canadiens.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Si vous choisissez le <strong>virement Interac</strong>, vous l'effectuez
                    depuis votre propre institution financière vers notre adresse courriel. Nous
                    ne recevons aucune coordonnée bancaire : seulement l'avis de virement. Votre
                    inscription reste « en attente » jusqu'à la réception du montant.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph sx={{ fontWeight: 600 }}>
                    Paiement par carte
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Au moment de payer, vous êtes redirigé vers une page hébergée par Stripe.
                    Votre numéro de carte, sa date d'expiration et son code de sécurité sont
                    saisis chez Stripe et <strong>ne transitent jamais par nos serveurs</strong> :
                    nous ne les voyons pas et ne pouvons pas les conserver.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Nous recevons uniquement de Stripe la confirmation que le paiement a réussi,
                    accompagnée du montant, de la devise et d'un identifiant de transaction. Ces
                    éléments servent à confirmer l'inscription et à produire la facture.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Stripe est un prestataire établi aux États-Unis. Vos renseignements de
                    paiement sont donc traités à l'extérieur du Québec, selon les propres
                    engagements de confidentialité de Stripe.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Les taxes applicables (TPS et TVQ) sont calculées et détaillées avant la
                    validation du paiement.
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>

              {/* 5. Partage des renseignements */}
              <StyledAccordion expanded={expanded === 'panel5'} onChange={handleAccordionChange('panel5')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>5. Partage des renseignements</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Les renseignements personnels ne sont ni vendus ni loués.</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Ils peuvent être communiqués uniquement :
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 1,
                    pl: 2
                  }}>
                    <Typography variant="body2">• <strong>À la Croix-Rouge canadienne</strong>, pour l'émission des certifications : nom, prénom et courriel des participants d'une séance</Typography>
                    <Typography variant="body2">• <strong>À Stripe</strong> (États-Unis), pour l'encaissement du paiement</Typography>
                    <Typography variant="body2">• <strong>À notre hébergeur</strong>, qui stocke le site, la base de données et les courriels</Typography>
                    <Typography variant="body2">• Si requis par la loi</Typography>
                    <Typography variant="body2">• Aux autorités compétentes en cas d'obligation légale</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Nos outils d'aide à la rédaction et d'analyse font appel à des services
                    d'intelligence artificielle. Ils ne reçoivent <strong>ni nom, ni courriel,
                    ni adresse</strong> : le détail figure à la section « Intelligence
                    artificielle » ci-dessous.
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>

              {/* 6. Conservation des données */}
              <StyledAccordion expanded={expanded === 'panel6'} onChange={handleAccordionChange('panel6')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>6. Conservation des données</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Les renseignements sont conservés :
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 1,
                    pl: 2
                  }}>
                    <Typography variant="body2">• <strong>Dossier d'apprenant et inscriptions</strong> : aussi longtemps que nécessaire à la gestion des formations et au suivi des certifications</Typography>
                    <Typography variant="body2">• <strong>Journal de sécurité</strong> (adresses IP et opérations) : 12 mois, puis suppression automatique</Typography>
                    <Typography variant="body2">• <strong>Avis internes</strong> affichés à notre personnel : supprimés automatiquement au bout d'un mois</Typography>
                    <Typography variant="body2">• <strong>Préinscriptions non finalisées</strong> : conservées tant qu'elles peuvent aboutir à une inscription, puis supprimables sur demande</Typography>
                    <Typography variant="body2">• Conformément aux obligations légales applicables au Québec</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Les données peuvent être archivées pour des fins administratives ou légales.
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>

              {/* 7. Intelligence artificielle */}
              <StyledAccordion expanded={expanded === 'panel11'} onChange={handleAccordionChange('panel11')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>7. Intelligence artificielle</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Le site utilise l'intelligence artificielle à deux endroits, avec une règle
                    simple : <strong>aucune donnée permettant de vous identifier n'est envoyée à
                    ces services.</strong>
                  </Typography>

                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 2 }}>
                    L'assistant de discussion et l'aide à la rédaction
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    L'assistant disponible sur le site répond à partir de notre catalogue de
                    formations. Le texte que vous saisissez lui est transmis pour produire la
                    réponse. <strong>N'y écrivez pas de renseignements personnels ou médicaux</strong> :
                    pour cela, utilisez le formulaire d'inscription ou écrivez-nous directement.
                    La même mécanique s'applique au formulaire de contact, qui peut vous aider à
                    formuler votre message avant l'envoi.
                  </Typography>

                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 2 }}>
                    Les analyses internes
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Notre personnel dispose de rapports d'analyse générés automatiquement.
                    Ces rapports ne portent que sur des <strong>totaux et des pourcentages</strong> :
                    nombre d'inscriptions, taux de confirmation, séances peu remplies. Lorsqu'un
                    point demande une vérification, il est désigné par un numéro d'enregistrement,
                    jamais par un nom. Aucun nom, courriel, adresse, allergie ou besoin particulier
                    n'est transmis.
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Ces services sont exploités par des fournisseurs établis aux États-Unis. Les
                    éléments qui leur sont transmis y sont donc traités à l'extérieur du Québec.
                    Les décisions concernant votre inscription sont prises par notre personnel :
                    <strong> aucune décision vous concernant n'est automatisée.</strong>
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>

              {/* 8. Journal de sécurité */}
              <StyledAccordion expanded={expanded === 'panel12'} onChange={handleAccordionChange('panel12')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>8. Journal de sécurité et adresse IP</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Chaque création, modification ou suppression d'une donnée dans notre système
                    est consignée dans un journal interne, ainsi que les tentatives de connexion
                    à l'espace d'administration.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Ce journal retient la date, la nature de l'opération, l'adresse IP et le type
                    de navigateur utilisé. Il sert exclusivement à la sécurité : détecter un accès
                    non autorisé et savoir qui est intervenu sur un dossier en cas de litige.
                    Il n'est jamais utilisé à des fins commerciales ni pour vous profiler.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Le pays d'origine</strong> de l'adresse IP est déterminé à l'aide
                    d'une base de correspondances installée sur notre propre serveur. Votre
                    adresse IP <strong>n'est transmise à aucun service tiers</strong> pour être
                    localisée. Cette information reste approximative et ne permet pas de vous
                    situer précisément.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Les mots de passe ne figurent jamais dans ce journal.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, color: '#c62828' }}>
                    Les entrées de plus de 12 mois sont supprimées automatiquement.
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>

              {/* 9. Préinscription */}
              <StyledAccordion expanded={expanded === 'panel13'} onChange={handleAccordionChange('panel13')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>9. Préinscription et panier abandonné</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Nous préférons vous le dire clairement : lorsque vous remplissez le formulaire
                    d'inscription et l'ajoutez à votre panier, <strong>les renseignements saisis
                    sont enregistrés à ce moment-là</strong>, avant même le paiement.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Cette préinscription sert de filet : si le paiement échoue ou si vous quittez
                    le site en cours de route, votre place et vos informations ne sont pas perdues,
                    et nous pouvons vous aider à finaliser. Elle porte la mention « en attente »
                    tant que l'inscription n'est pas confirmée.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vous pouvez à tout moment demander la suppression d'une préinscription qui
                    n'a pas abouti, en écrivant à{' '}
                    <Link href="mailto:info@vitasecours.ca" sx={{ color: '#c62828' }}>info@vitasecours.ca</Link>.
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>

              {/* 10. Droits des utilisateurs (Loi 25) */}
              <StyledAccordion expanded={expanded === 'panel7'} onChange={handleAccordionChange('panel7')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>10. Droits des utilisateurs (Loi 25 – Québec)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Toute personne peut :
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 1,
                    mb: 2
                  }}>
                    <Typography variant="body2">• Demander l'accès à ses renseignements personnels</Typography>
                    <Typography variant="body2">• Demander la rectification de données inexactes</Typography>
                    <Typography variant="body2">• Retirer son consentement lorsque applicable</Typography>
                    <Typography variant="body2">• Demander la suppression de ses renseignements, sous réserve des obligations légales</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Les demandes doivent être envoyées à : <Link href="mailto:info@vitasecours.ca" sx={{ color: '#c62828' }}>info@vitasecours.ca</Link>
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>

              {/* 8. Mineurs */}
              <StyledAccordion expanded={expanded === 'panel8'} onChange={handleAccordionChange('panel8')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>11. Mineurs</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Les formations étant accessibles aux mineurs, les renseignements d'un responsable légal peuvent être collectés lorsque requis.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Le consentement du parent ou tuteur est requis lorsque la loi l'exige.
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>

              {/* 9. Cookies */}
              <StyledAccordion expanded={expanded === 'panel9'} onChange={handleAccordionChange('panel9')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>12. Cookies</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <CookieIcon sx={{ color: '#c62828', fontSize: 32 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Le site n'utilise que ce qui lui est nécessaire pour fonctionner :
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        pl: 2
                      }}>
                        <Typography variant="body2">• Le souvenir de votre réponse au bandeau de consentement</Typography>
                        <Typography variant="body2">• Le contenu de votre panier et, le cas échéant, votre session de connexion</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 600, color: '#c62828' }}>
                        Aucun outil de mesure d'audience n'est actif : nous ne suivons pas les
                        pages que vous consultez et ne transmettons rien à une régie publicitaire.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Le détail complet, ainsi que le moyen de revenir sur votre choix, figure
                        sur notre{' '}
                        <Link component={RouterLink} to="/politique-cookies" sx={{ color: '#c62828' }}>
                          politique de cookies
                        </Link>. Vous pouvez également tout effacer depuis les réglages de votre
                        navigateur.
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </StyledAccordion>

              {/* 10. Modifications */}
              <StyledAccordion expanded={expanded === 'panel10'} onChange={handleAccordionChange('panel10')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>13. Modifications</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Cette politique peut être modifiée à tout moment afin de refléter les changements législatifs.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    La date de mise à jour sera ajustée en conséquence.
                  </Typography>
                </AccordionDetails>
              </StyledAccordion>
            </Box>

            {/* Coordonnées - Flexbox */}
            <Box sx={{ width: '100%', mb: 6 }}>
              <SectionTitle variant="h4">
                <LocationOnIcon /> Coordonnées
              </SectionTitle>
              
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: 'center'
              }}>
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 0' } }}>
                  <ContactCard>
                    <LocationOnIcon sx={{ color: '#c62828', fontSize: 48, mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Adresse</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      275 Bd des Laurentides<br />
                      Saint-Jérôme (QC) J7Z 7K8<br />
                      Canada
                    </Typography>
                  </ContactCard>
                </Box>
                
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 0' } }}>
                  <ContactCard>
                    <EmailIcon sx={{ color: '#c62828', fontSize: 48, mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Courriel</Typography>
                    <Link href="mailto:info@vitasecours.ca" sx={{ color: '#c62828', textDecoration: 'none', fontWeight: 500 }}>
                      info@vitasecours.ca
                    </Link>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      Pour toutes demandes d'accès ou rectification
                    </Typography>
                  </ContactCard>
                </Box>
              </Box>
            </Box>

            {/* Note de bas de page */}
            <FooterNote>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>vitasecours.ca</strong> - Formation en secourisme, partenaire de la Croix-Rouge canadienne
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Exploité par <strong>Amira Ben Nasr</strong> - Conforme à la Loi 25 du Québec
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0'
              }}>
                <Typography variant="caption" color="text.secondary">
                  © {currentYear} vitasecours.ca
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  275 Bd des Laurentides, Saint-Jérôme, QC J7Z 7K8
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <Link href="mailto:info@vitasecours.ca" sx={{ color: '#666' }}>info@vitasecours.ca</Link>
                </Typography>
              </Box>
            </FooterNote>

            {/* Bouton retour */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                component={RouterLink}
                to="/"
                variant="outlined"
                startIcon={<HomeIcon />}
                sx={{
                  borderColor: '#c62828',
                  color: '#c62828',
                  px: 4,
                  py: 1.5,
                  borderRadius: '50px',
                  '&:hover': {
                    borderColor: '#8e0000',
                    backgroundColor: 'rgba(198, 40, 40, 0.05)',
                  }
                }}
              >
                Retour à l'accueil
              </Button>
            </Box>
          </StyledPaper>
        </PageContainer>
      </HeroSection>
    </Box>
  );
};

export default PolitiqueConfidentialite;